<template>
  <transition name="pcm-fade">
    <div
      v-if="visible"
      ref="root"
      class="pcm-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pcm-title"
      tabindex="-1"
      @keydown.capture="onKeydown"
      @mousedown.self="onBackdrop"
    >
      <div class="pcm-panel" role="document" @mousedown.stop>
        <!-- Head -->
        <header class="pcm-head">
          <span class="pcm-head__badge" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
          </span>
          <div class="pcm-head__text">
            <h2 id="pcm-title">Complete your profile</h2>
            <p v-if="mandatory">
              These details are required for every staff member. You’ve used
              all your reminders, so you can’t continue using the OMS until
              they’re filled in.
            </p>
            <p v-else>
              These details are required for every staff member. You can
              finish this later, but after
              <b>{{ warningsRemaining }} more {{ warningsRemaining === 1 ? 'reminder' : 'reminders' }}</b>
              it becomes compulsory.
            </p>
          </div>
          <span class="pcm-head__count">{{ filledCount }}/{{ totalCount }}</span>
          <button
            v-if="!mandatory"
            type="button"
            class="pcm-head__close"
            aria-label="Remind me later"
            @click="dismiss"
          >
            <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </header>

        <div class="pcm-progress" role="progressbar" :aria-valuenow="progressPct" aria-valuemin="0" aria-valuemax="100">
          <div class="pcm-progress__bar" :style="{ width: progressPct + '%' }"></div>
        </div>

        <!-- Step rail -->
        <nav v-if="steps.length > 1" class="pcm-rail" aria-label="Sections">
          <button
            v-for="(step, index) in steps"
            :key="step.key"
            type="button"
            :class="['pcm-rail__item', {
              'pcm-rail__item--active': index === stepIndex,
              'pcm-rail__item--done': stepIsComplete(step) && index !== stepIndex,
            }]"
            @click="stepIndex = index"
          >
            <span class="pcm-rail__mark" aria-hidden="true">
              <svg v-if="stepIsComplete(step) && index !== stepIndex" viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" stroke-width="3.4" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              <template v-else>{{ index + 1 }}</template>
            </span>
            <span class="pcm-rail__label">{{ step.title }}</span>
          </button>
        </nav>

        <!-- Body -->
        <div class="pcm-body">
          <div v-if="isLoading" class="pcm-loading">
            <span class="pcm-spinner" aria-hidden="true"></span>
            <span>Loading your details…</span>
          </div>

          <template v-else-if="currentStep">
            <p v-if="errorMessage" class="pcm-error-banner">{{ errorMessage }}</p>

            <h3 class="pcm-step-title">{{ currentStep.title }}</h3>
            <p class="pcm-step-caption">{{ currentStep.caption }}</p>

            <!-- Photo step -->
            <div v-if="currentStep.key === 'photo'" class="pcm-photo">
              <div
                class="pcm-photo__avatar"
                :class="{ 'pcm-photo__avatar--empty': !showPhoto }"
                :style="!showPhoto ? { background: avatarGradient } : null"
              >
                <img v-if="showPhoto" :src="photoPreview" alt="Your profile photo" @error="photoLoadFailed = true" />
                <span v-else>{{ avatarInitials }}</span>
              </div>
              <input
                ref="photoInput"
                type="file"
                accept="image/png,image/jpeg,image/webp"
                class="pcm-photo__input"
                @change="onPhotoSelected"
              />
              <button type="button" class="pcm-btn pcm-btn--ghost" @click="triggerPhotoPicker">
                {{ showPhoto ? 'Choose a different photo' : 'Upload your photo' }}
              </button>
              <p class="pcm-photo__hint">
                A clear head-and-shoulders photo. PNG, JPG or WebP, up to 2&nbsp;MB.
              </p>
              <p v-if="savedPhotoBroken" class="pcm-photo__stale">
                We couldn’t load your saved photo — please upload it again.
              </p>
              <p v-if="fieldErrors.image" class="pcm-field__error pcm-field__error--center">{{ fieldErrors.image }}</p>
            </div>

            <!-- Field steps -->
            <div v-else class="pcm-grid">
              <div
                v-for="field in currentStep.fields"
                :key="field.key"
                :class="['pcm-field', { 'pcm-field--wide': field.wide }]"
              >
                <label class="pcm-field__label" :for="'pcm-' + field.key">
                  {{ field.label }}
                  <span class="pcm-field__req" aria-hidden="true">*</span>
                </label>

                <!-- Native select and date input on purpose: AppDropdown
                     re-parents its panel to <body> at z-index 1080, which
                     would land behind this overlay and outside its focus
                     trap. Native controls also give mobile staff the OS
                     pickers. -->
                <select
                  v-if="field.type === 'select'"
                  :id="'pcm-' + field.key"
                  v-model="form[field.key]"
                  class="pcm-input pcm-input--select"
                  :class="{ 'pcm-input--error': fieldErrors[field.key] }"
                  required
                >
                  <option value="" disabled>{{ field.placeholder || 'Select' }}</option>
                  <option v-for="option in field.options" :key="option.value" :value="option.value">
                    {{ option.label }}
                  </option>
                </select>
                <input
                  v-else-if="field.type === 'date'"
                  :id="'pcm-' + field.key"
                  v-model="form[field.key]"
                  type="date"
                  class="pcm-input"
                  :class="{ 'pcm-input--error': fieldErrors[field.key] }"
                  :max="todayIso"
                />
                <textarea
                  v-else-if="field.type === 'textarea'"
                  :id="'pcm-' + field.key"
                  v-model="form[field.key]"
                  rows="3"
                  class="pcm-input pcm-input--textarea"
                  :class="{ 'pcm-input--error': fieldErrors[field.key] }"
                  :placeholder="field.placeholder"
                ></textarea>
                <input
                  v-else
                  :id="'pcm-' + field.key"
                  v-model="form[field.key]"
                  :type="field.type || 'text'"
                  class="pcm-input"
                  :class="{ 'pcm-input--error': fieldErrors[field.key] }"
                  :placeholder="field.placeholder"
                  :inputmode="field.inputmode"
                  :maxlength="field.maxlength"
                  :autocomplete="field.autocomplete || 'off'"
                />

                <span v-if="fieldErrors[field.key]" class="pcm-field__error">{{ fieldErrors[field.key] }}</span>
                <span v-else-if="field.hint" class="pcm-field__hint">{{ field.hint }}</span>
              </div>
            </div>
          </template>

          <!-- The required list never arrived (the /api/profile load failed).
               Nothing can be filled in until it does. -->
          <div v-else class="pcm-loading pcm-loading--failed">
            <span>{{ errorMessage || 'We could not load the details you need to fill in.' }}</span>
          </div>
        </div>

        <!-- Foot -->
        <footer class="pcm-foot">
          <button
            v-if="!mandatory"
            type="button"
            class="pcm-link pcm-link--defer"
            :disabled="isSaving || isDismissing"
            @click="dismiss"
          >{{ isDismissing ? 'Saving…' : 'Remind me later' }}</button>
          <button v-else type="button" class="pcm-link" :disabled="isSaving" @click="signOut">
            Sign out instead
          </button>

          <div class="pcm-foot__actions">
            <button
              v-if="stepIndex > 0"
              type="button"
              class="pcm-btn pcm-btn--ghost"
              :disabled="isSaving"
              @click="goBack"
            >Back</button>
            <button
              v-if="steps.length && !isLastStep"
              type="button"
              class="pcm-btn pcm-btn--primary"
              :disabled="isLoading"
              @click="goNext"
            >Continue</button>
            <button
              v-if="!steps.length"
              type="button"
              class="pcm-btn pcm-btn--primary"
              :disabled="isLoading"
              @click="loadProfile"
            >Try again</button>
            <button
              v-else
              type="button"
              class="pcm-btn pcm-btn--primary"
              :disabled="isLoading || isSaving"
              @click="submit"
            >
              <span v-if="isSaving" class="pcm-spinner pcm-spinner--sm" aria-hidden="true"></span>
              <span>{{ isSaving ? 'Saving…' : 'Save & continue' }}</span>
            </button>
          </div>
        </footer>
      </div>
    </div>
  </transition>
</template>

<script>
import axios from 'axios'
import posthog from 'posthog-js'
import completion, { applyCompletion, deferGate, resetCompletion } from '@/utils/profileCompletion'

const PHOTO_MAX_BYTES = 2 * 1024 * 1024

const toOptions = (values) => values.map((value) => ({ value, label: value }))

const GENDER_OPTIONS = toOptions(['Male', 'Female', 'Other', 'Prefer not to say'])
const MARITAL_OPTIONS = toOptions(['Single', 'Married', 'Divorced', 'Widowed', 'Separated'])
const BLOOD_GROUP_OPTIONS = toOptions(['A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-'])
const RELATION_OPTIONS = toOptions(['Father', 'Mother', 'Spouse', 'Sibling', 'Child', 'Guardian', 'Friend', 'Other'])

// The server (config/profile.php) decides *which* of these are required; this
// map only says how each one is rendered and which step it belongs to. A
// required key with no entry here still shows up — as a plain text input in
// the "Other details" step — so adding a field server-side never leaves the
// user facing a gate they cannot clear.
const FIELD_DEFS = {
  image: { step: 'photo' },

  phone: { step: 'personal', type: 'tel', placeholder: '98xxxxxxxx', autocomplete: 'tel', inputmode: 'tel' },
  alternate_phone: { step: 'personal', type: 'tel', inputmode: 'tel' },
  date_of_birth: { step: 'personal', type: 'date' },
  gender: { step: 'personal', type: 'select', options: GENDER_OPTIONS, placeholder: 'Select gender' },
  blood_group: { step: 'personal', type: 'select', options: BLOOD_GROUP_OPTIONS, placeholder: 'Select group' },
  marital_status: { step: 'personal', type: 'select', options: MARITAL_OPTIONS, placeholder: 'Select status' },

  address: { step: 'address', type: 'textarea', wide: true, placeholder: 'House / building, street, locality' },
  city: { step: 'address' },
  state: { step: 'address' },
  pincode: { step: 'address', inputmode: 'numeric', maxlength: 6, placeholder: '6 digits', transform: 'digits' },
  country: { step: 'address', placeholder: 'India' },

  emergency_contact_name: { step: 'emergency', autocomplete: 'off' },
  emergency_contact_phone: { step: 'emergency', type: 'tel', inputmode: 'tel' },
  emergency_contact_relation: { step: 'emergency', type: 'select', options: RELATION_OPTIONS, placeholder: 'Select relation' },

  pan_number: { step: 'kyc', maxlength: 10, placeholder: 'ABCDE1234F', transform: 'upper', hint: 'Exactly as printed on your PAN card.' },
  // maxlength 14, not 12, so "1234 5678 9012" fits — the spaces are stripped
  // on submit and again on the server.
  aadhaar_number: { step: 'kyc', inputmode: 'numeric', maxlength: 14, placeholder: '1234 5678 9012', transform: 'digits' },

  bank_account_number: { step: 'bank', inputmode: 'numeric', maxlength: 20, transform: 'compact' },
  bank_name: { step: 'bank', placeholder: 'e.g. State Bank of India' },
  bank_ifsc: { step: 'bank', maxlength: 11, placeholder: 'SBIN0001234', transform: 'upper', hint: '11 characters, from your cheque book or passbook.' },
}

const STEP_META = [
  { key: 'photo', title: 'Your photo', caption: 'This is how your colleagues will recognise you across the OMS.' },
  { key: 'personal', title: 'Personal details', caption: 'How we reach you, and the basics HR needs on file.' },
  { key: 'address', title: 'Your address', caption: 'Where you are based.' },
  { key: 'emergency', title: 'Emergency contact', caption: 'Who we should call if something happens at work.' },
  { key: 'kyc', title: 'Identity', caption: 'Exactly as printed on your identity document. Visible only to HR and payroll.' },
  { key: 'bank', title: 'Bank account', caption: 'Where your salary is paid. Visible only to HR and payroll.' },
  { key: 'other', title: 'Other details', caption: 'A few remaining details we need from you.' },
]

// Per-field format checks layered on top of "must not be empty".
const VALIDATORS = {
  phone: (value) => (digitsOf(value).length >= 10 ? '' : 'Enter a valid phone number (at least 10 digits).'),
  alternate_phone: (value) => (digitsOf(value).length >= 10 ? '' : 'Enter a valid phone number (at least 10 digits).'),
  emergency_contact_phone: (value) => (digitsOf(value).length >= 10 ? '' : 'Enter a valid phone number (at least 10 digits).'),
  pincode: (value) => (/^\d{6}$/.test(digitsOf(value)) ? '' : 'Pincode must be 6 digits.'),
  aadhaar_number: (value) => (/^\d{12}$/.test(digitsOf(value)) ? '' : 'Aadhaar must be 12 digits.'),
  pan_number: (value) => (/^[A-Z]{5}\d{4}[A-Z]$/.test(String(value).toUpperCase()) ? '' : 'Enter a valid PAN (e.g. ABCDE1234F).'),
  bank_ifsc: (value) => (/^[A-Z]{4}0[A-Z0-9]{6}$/.test(String(value).toUpperCase()) ? '' : 'Enter a valid IFSC (e.g. SBIN0001234).'),
  // Deliberately loose — account-number formats vary by bank, and a false
  // rejection here locks someone out of the whole OMS.
  bank_account_number: (value) => (/^[A-Za-z0-9]{6,20}$/.test(String(value).replace(/\s+/g, '')) ? '' : 'Enter a valid bank account number.'),
  date_of_birth: (value) => {
    const parsed = new Date(`${value}T00:00:00`)
    if (Number.isNaN(parsed.getTime())) return 'Enter a valid date of birth.'
    const now = new Date()
    if (parsed > now) return 'Date of birth cannot be in the future.'
    const years = (now - parsed) / (365.25 * 24 * 60 * 60 * 1000)
    if (years < 15) return 'Date of birth looks incorrect.'
    if (years > 100) return 'Date of birth looks incorrect.'
    return ''
  },
}

// Mirrors config/profile.php's `normalise` map, so what the modal validates
// is exactly what the server stores.
function applyTransform (transform, value) {
  if (transform === 'upper') return value.toUpperCase()
  if (transform === 'digits') return value.replace(/\D/g, '')
  if (transform === 'compact') return value.replace(/\s+/g, '')
  return value
}

function digitsOf (value) {
  return String(value == null ? '' : value).replace(/\D/g, '')
}

function humanise (key) {
  return String(key)
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

function pickDateOnly (value) {
  if (!value) return ''
  const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/)
  return match ? match[1] : ''
}

export default {
  name: 'ProfileCompletionModal',
  data () {
    return {
      gate: completion.state,
      isLoading: false,
      isSaving: false,
      isDismissing: false,
      hasLoaded: false,
      stepIndex: 0,
      errorMessage: '',
      fieldErrors: {},
      form: {},
      identity: { name: '', email: '' },
      photoExisting: '',
      photoFile: null,
      photoPreview: '',
      photoLoadFailed: false,
    }
  },
  computed: {
    visible () {
      if (!this.gate.known || this.gate.complete || this.gate.enforced === false) return false
      // Still a reminder and already dismissed this session — stay quiet.
      return this.mandatory || !this.gate.deferred
    },
    /** True once the warnings are spent: no close button, no way out but Save. */
    mandatory () {
      return this.gate.mandatory === true
    },
    warningsRemaining () {
      return this.gate.warningsRemaining
    },
    // Required keys, in the server's configured order, resolved into
    // renderable field descriptors.
    fields () {
      return (this.gate.required || []).map((key) => {
        const def = FIELD_DEFS[key] || { step: 'other' }
        return {
          ...def,
          key,
          label: this.gate.labels[key] || humanise(key),
          options: def.options || [],
        }
      })
    },
    steps () {
      return STEP_META
        .map((meta) => ({ ...meta, fields: this.fields.filter((field) => field.step === meta.key) }))
        .filter((step) => step.fields.length > 0)
    },
    currentStep () {
      return this.steps[this.stepIndex] || null
    },
    isLastStep () {
      return this.stepIndex >= this.steps.length - 1
    },
    totalCount () {
      return this.fields.length
    },
    filledCount () {
      return this.fields.filter((field) => this.isFieldFilled(field.key)).length
    },
    progressPct () {
      if (!this.totalCount) return 0
      return Math.round((this.filledCount / this.totalCount) * 100)
    },
    todayIso () {
      const now = new Date()
      const pad = (n) => String(n).padStart(2, '0')
      return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
    },
    showPhoto () {
      return !!this.photoPreview && !this.photoLoadFailed
    },
    /**
     * The stored photo URL is set but the image won't load — the R2 object is
     * gone or the URL is bad. Treated as "no photo": otherwise the step counts
     * as done while the user is looking at their initials.
     */
    savedPhotoBroken () {
      return !this.photoFile && !!this.photoExisting && this.photoLoadFailed
    },
    avatarInitials () {
      const source = this.identity.name || this.identity.email || 'U'
      const initials = source.split(/\s+/).filter(Boolean).slice(0, 2).map((word) => word[0].toUpperCase()).join('')
      return initials || 'U'
    },
    avatarGradient () {
      const palette = [
        'linear-gradient(135deg, #0e5b46 0%, #16a07a 100%)',
        'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
        'linear-gradient(135deg, #6d28d9 0%, #a855f7 100%)',
        'linear-gradient(135deg, #b45309 0%, #f59e0b 100%)',
      ]
      const seed = String(this.identity.name || this.identity.email || '?')
      let hash = 0
      for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0
      return palette[hash % palette.length]
    },
  },
  watch: {
    fields: {
      immediate: true,
      handler (fields) {
        fields.forEach((field) => {
          if (field.key !== 'image' && !(field.key in this.form)) {
            this.$set(this.form, field.key, '')
          }
        })
      },
    },
    stepIndex () {
      if (this.visible) this.$nextTick(this.focusFirstField)
    },
    visible: {
      immediate: true,
      handler (now) {
        if (now) {
          this.onOpen()
        } else {
          this.onClose()
        }
      },
    },
  },
  beforeDestroy () {
    this.onClose()
  },
  methods: {
    onOpen () {
      if (!this.hasLoaded) this.loadProfile()
      this._prevBodyOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      this.$nextTick(this.focusFirstField)
    },
    onClose () {
      if (this._prevBodyOverflow !== undefined) {
        document.body.style.overflow = this._prevBodyOverflow
        this._prevBodyOverflow = undefined
      }
    },
    /**
     * Put the caret in the step's first input so typing just works. Scoped to
     * the body, otherwise focus lands on the step rail above it.
     */
    focusFirstField () {
      const target = this.focusableElements('.pcm-body')[0] || this.focusableElements()[0]
      if (target) target.focus()
      else if (this.$refs.root) this.$refs.root.focus()
    },
    focusableElements (within) {
      const root = this.$refs.root
      if (!root) return []
      const scope = within ? root.querySelector(within) : root
      if (!scope) return []
      const selector = [
        'a[href]',
        'button:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
      ].join(',')
      // offsetParent filters out anything display:none on the inactive steps.
      return Array.from(scope.querySelectorAll(selector)).filter((el) => el.offsetParent !== null)
    },
    /**
     * Tab cycles within the gate instead of reaching the app behind it.
     *
     * This replaced a document-level `focus` listener that called
     * `root.focus()` whenever focus landed outside the dialog. That version
     * could steal focus away from the very inputs it was meant to protect,
     * which left the modal impossible to type into — don't reintroduce it.
     */
    trapTab (event) {
      const focusable = this.focusableElements()
      if (focusable.length === 0) return

      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      const active = document.activeElement

      if (event.shiftKey && (active === first || !this.$refs.root.contains(active))) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      }
    },
    // Deliberately no Escape handler: this dialog cannot be dismissed. Enter
    // advances instead of submitting a half-filled form.
    onKeydown (event) {
      if (event.key === 'Escape') {
        event.preventDefault()
        event.stopPropagation()
        // Escape defers while reminders remain; once mandatory it does nothing.
        if (!this.mandatory) this.dismiss()
        return
      }
      if (event.key === 'Tab') {
        this.trapTab(event)
        return
      }
      if (event.key === 'Enter' && event.target && event.target.tagName !== 'TEXTAREA') {
        event.preventDefault()
        if (!this.steps.length) return
        if (this.isLastStep) this.submit()
        else this.goNext()
      }
    },

    loadProfile () {
      this.isLoading = true
      this.errorMessage = ''

      axios.get(`${this.$apiAdress}/api/profile`)
        .then((response) => {
          const data = response.data || {}
          this.identity = { name: data.name || '', email: data.email || '' }

          const next = {}
          this.fields.forEach((field) => {
            if (field.key === 'image') return
            const value = data[field.key]
            next[field.key] = field.type === 'date' ? pickDateOnly(value) : (value == null ? '' : String(value))
          })
          this.form = next

          this.photoExisting = typeof data.image === 'string' ? data.image : ''
          this.photoPreview = this.photoExisting
          this.photoFile = null
          this.photoLoadFailed = false

          applyCompletion(data.profile_completion)
          this.hasLoaded = true
          this.jumpToFirstIncompleteStep()
          // The fields only exist now that isLoading has cleared.
          this.$nextTick(this.focusFirstField)
        })
        .catch((error) => {
          this.errorMessage = (error && error.response && error.response.data && error.response.data.message)
            || 'We could not load your details. Check your connection and try again.'
        })
        .finally(() => {
          this.isLoading = false
        })
    },

    isFieldFilled (key) {
      if (key === 'image') return !!this.photoFile || (!!this.photoExisting && !this.photoLoadFailed)
      const value = this.form[key]
      return value != null && String(value).trim() !== ''
    },
    // Always the strict reading: the rail ticks and the progress bar should
    // show real completion, not "acceptable for now".
    stepIsComplete (step) {
      return step.fields.every((field) => this.isFieldFilled(field.key) && !this.validateField(field.key))
    },
    jumpToFirstIncompleteStep () {
      const index = this.steps.findIndex((step) => !this.stepIsComplete(step))
      this.stepIndex = index === -1 ? 0 : index
    },

    /**
     * Returns an error string, or '' when the field is acceptable.
     *
     * `requireValue` is what separates the two modes. During the grace period
     * an empty field is fine — you may save what you have — but anything you
     * did type still has to be well-formed. Once the gate is mandatory,
     * blank is an error too.
     */
    validateField (key, requireValue = true) {
      const field = this.fields.find((item) => item.key === key)
      const label = (field && field.label) || humanise(key)

      if (!this.isFieldFilled(key)) {
        if (!requireValue) return ''
        return key === 'image' ? 'Please upload a profile photo.' : `${label} is required.`
      }
      if (key === 'image') return ''

      const validator = VALIDATORS[key]
      return validator ? validator(this.form[key]) : ''
    },
    validateStep (step, requireValue = true) {
      const errors = {}
      step.fields.forEach((field) => {
        const error = this.validateField(field.key, requireValue)
        if (error) errors[field.key] = error
      })
      this.fieldErrors = { ...this.fieldErrors, ...errors }
      step.fields.forEach((field) => {
        if (!errors[field.key]) this.$delete(this.fieldErrors, field.key)
      })
      return Object.keys(errors).length === 0
    },

    goNext () {
      if (!this.currentStep) return
      this.errorMessage = ''
      if (!this.validateStep(this.currentStep, this.mandatory)) {
        this.errorMessage = this.mandatory
          ? 'Please fill in the highlighted fields to continue.'
          : 'Please correct the highlighted fields to continue.'
        return
      }
      if (!this.isLastStep) this.stepIndex += 1
    },
    goBack () {
      this.errorMessage = ''
      if (this.stepIndex > 0) this.stepIndex -= 1
    },

    triggerPhotoPicker () {
      if (this.$refs.photoInput) this.$refs.photoInput.click()
    },
    onPhotoSelected (event) {
      const file = event.target.files && event.target.files[0]
      event.target.value = ''
      if (!file) return

      if (file.size > PHOTO_MAX_BYTES) {
        this.$set(this.fieldErrors, 'image', 'That image is larger than 2 MB. Please choose a smaller one.')
        return
      }
      this.$delete(this.fieldErrors, 'image')
      this.errorMessage = ''
      this.photoFile = file
      this.photoLoadFailed = false

      const reader = new FileReader()
      reader.onload = (e) => { this.photoPreview = e.target.result }
      reader.readAsDataURL(file)
    },

    submit () {
      this.errorMessage = ''

      // Validate every step, not just the last — the rail lets users jump
      // around, so the final button is the only place completeness is certain.
      // While reminders remain we only police the format of what was actually
      // filled in; blanks are the user's business until the gate turns
      // mandatory.
      const stepResults = this.steps.map((step) => this.validateStep(step, this.mandatory))
      const firstBadIndex = stepResults.indexOf(false)
      if (firstBadIndex !== -1) {
        this.stepIndex = firstBadIndex
        this.errorMessage = this.mandatory
          ? 'Please fill in the highlighted fields before saving.'
          : 'Please correct the highlighted fields before saving.'
        return
      }

      const payload = new FormData()
      // `enforce_completion` makes the server demand the whole profile;
      // `partial_completion` asks it to keep what it's given and only check
      // the format of it.
      payload.append(this.mandatory ? 'enforce_completion' : 'partial_completion', '1')

      this.fields.forEach((field) => {
        if (field.key === 'image') return
        let value = this.form[field.key]
        value = value == null ? '' : String(value).trim()
        value = applyTransform(field.transform, value)

        // A blank reads as "clear this" to the server, which it refuses for a
        // field you've already given. On a partial save just omit it.
        if (value === '' && !this.mandatory) return

        payload.append(field.key, value)
      })

      if (this.photoFile) payload.append('profile_photo', this.photoFile)

      this.isSaving = true

      axios.post(`${this.$apiAdress}/api/profile`, payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
        .then((response) => {
          const data = response.data || {}
          this.syncLocalAuthUser(data)
          this.hasLoaded = false
          this.photoFile = null

          const nowComplete = !!(data.profile_completion && data.profile_completion.complete)

          if (this.$toast) {
            if (nowComplete) {
              this.$toast.success('Profile completed', { description: 'Thanks — you’re all set.' })
            } else {
              this.$toast.success('Progress saved', {
                description: 'Your details so far have been saved.',
              })
            }
          }
          try {
            posthog.capture('profile_completion_submitted', {
              fields: this.totalCount,
              complete: nowComplete,
            })
          } catch (e) {
            // Analytics must never block the gate from closing.
          }

          if (nowComplete) {
            // Applied last: this flips `visible` to false and unmounts us.
            applyCompletion(data.profile_completion)
            return
          }

          // Saved, but still short of the full set. Closing the gate on a
          // partial profile costs one reminder — same as "Remind me later" —
          // otherwise the grace period would never actually run out.
          this.acknowledgeWarning()
        })
        .catch((error) => {
          const response = error && error.response
          const data = (response && response.data) || {}

          if (response && response.status === 422 && data.errors) {
            const errors = {}
            Object.keys(data.errors).forEach((key) => {
              const value = data.errors[key]
              errors[key] = Array.isArray(value) ? value[0] : String(value)
            })
            this.fieldErrors = errors

            const badStep = this.steps.findIndex((step) => step.fields.some((field) => errors[field.key]))
            if (badStep !== -1) this.stepIndex = badStep
            this.errorMessage = data.message || 'Some of your details need attention.'
            return
          }

          this.errorMessage = data.message || 'We could not save your details. Please try again.'
        })
        .finally(() => {
          this.isSaving = false
        })
    },

    syncLocalAuthUser (data) {
      try {
        const raw = localStorage.getItem('auth_user')
        const current = raw ? JSON.parse(raw) : {}
        localStorage.setItem('auth_user', JSON.stringify({
          ...current,
          image: Object.prototype.hasOwnProperty.call(data, 'image') ? data.image : current.image,
          profile_completion: data.profile_completion || current.profile_completion,
        }))
      } catch (error) {
        // A stale cached avatar is harmless — /auth/me refreshes it on boot.
      }
    },

    onBackdrop () {
      if (!this.mandatory) this.dismiss()
    },

    /**
     * "Remind me later." Burns one of the user's reminders server-side and
     * hides the gate for the rest of this tab session. The server's reply
     * carries the refreshed count, so the last dismissal is what flips
     * `mandatory` on — from then on this button is gone.
     */
    dismiss () {
      if (this.mandatory || this.isDismissing) return
      this.isDismissing = true

      this.acknowledgeWarning()
    },

    acknowledgeWarning () {
      return axios.post(`${this.$apiAdress}/api/profile/completion/acknowledge`)
        .then((response) => {
          const payload = (response.data || {}).profile_completion
          // Defer first: applying the payload can flip `mandatory`, and a
          // deferral must never be able to hide a gate that just locked.
          deferGate(true)
          applyCompletion(payload)
        })
        .catch(() => {
          // Couldn't reach the server — still let them get on with their
          // work this session rather than trapping them behind a nag.
          deferGate(true)
        })
        .finally(() => {
          this.isDismissing = false
        })
    },

    // The one way out of a mandatory gate. Without it a user who genuinely
    // cannot supply a detail right now would be stuck on a screen with no exit.
    signOut () {
      this.performSignOut()
    },

    performSignOut () {
      const finish = () => {
        localStorage.removeItem('api_token')
        localStorage.removeItem('roles')
        localStorage.removeItem('auth_user')
        this.$store.commit('clearOrganisationContext')
        this.$store.commit('clearPermissions')
        resetCompletion()
        try {
          posthog.reset()
        } catch (e) {
          // Ignore — sign-out must not depend on analytics.
        }
        this.$router.push({ path: '/login' }).catch(() => {})
      }

      axios.post(`${this.$apiAdress}/api/logout`).then(finish).catch(finish)
    },
  },
}
</script>

<style scoped>
.pcm-overlay {
  position: fixed;
  inset: 0;
  /* Above all app chrome, below the toast stack (9999) so confirmations
     stay visible, and below the session-expiry overlay (99999). */
  z-index: 9990;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  background: rgba(9, 21, 17, 0.62);
  backdrop-filter: blur(4px);
  overflow-y: auto;
}

.pcm-panel {
  width: 100%;
  max-width: 680px;
  max-height: calc(100vh - 2.5rem);
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 24px 60px rgba(8, 23, 18, 0.32);
  overflow: hidden;
}

/* ── Head ── */
.pcm-head {
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
  padding: 1.15rem 1.35rem 1rem;
  background: linear-gradient(135deg, #0a4a3a 0%, #0e5b46 55%, #0a4a3a 100%);
  color: #ffffff;
}

.pcm-head__badge {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 11px;
  background: rgba(255, 255, 255, 0.14);
  color: #ffffff;
}

.pcm-head__text {
  flex: 1 1 auto;
  min-width: 0;
}

.pcm-head__text h2 {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  letter-spacing: -0.01em;
}

.pcm-head__text p {
  margin: 0.2rem 0 0;
  font-size: 0.78rem;
  line-height: 1.45;
  color: rgba(255, 255, 255, 0.82);
}

.pcm-head__count {
  flex: 0 0 auto;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.16);
  font-size: 0.72rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.pcm-head__close {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;
  border: 0;
  border-radius: 7px;
  background: rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.88);
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.pcm-head__close:hover {
  background: rgba(255, 255, 255, 0.22);
  color: #ffffff;
}

.pcm-head__close:focus {
  outline: none;
}

.pcm-head__close:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.45);
}

.pcm-link--defer:hover:not(:disabled) {
  color: #0e5b46;
}

.pcm-progress {
  height: 3px;
  background: #e6efeb;
}

.pcm-progress__bar {
  height: 100%;
  background: linear-gradient(90deg, #16a07a 0%, #34d399 100%);
  transition: width 0.28s ease;
}

/* ── Step rail ── */
.pcm-rail {
  display: flex;
  gap: 0.3rem;
  padding: 0.65rem 1.35rem;
  border-bottom: 1px solid #eef1f7;
  overflow-x: auto;
}

.pcm-rail__item {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.28rem 0.6rem;
  border: 1px solid transparent;
  border-radius: 999px;
  background: transparent;
  color: #6b7280;
  font-size: 0.72rem;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

.pcm-rail__item:hover {
  background: #f5fbf8;
  color: #0e5b46;
}

.pcm-rail__item--active {
  background: #eaf6f1;
  border-color: #cfe9de;
  color: #0e5b46;
}

.pcm-rail__item--done {
  color: #0e5b46;
}

.pcm-rail__mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 17px;
  height: 17px;
  border-radius: 50%;
  background: #e5e7eb;
  color: #4b5563;
  font-size: 0.62rem;
  font-weight: 700;
}

.pcm-rail__item--active .pcm-rail__mark {
  background: #0e5b46;
  color: #ffffff;
}

.pcm-rail__item--done .pcm-rail__mark {
  background: #16a07a;
  color: #ffffff;
}

/* ── Body ── */
.pcm-body {
  flex: 1 1 auto;
  min-height: 0;
  padding: 1.1rem 1.35rem 1.25rem;
  overflow-y: auto;
}

.pcm-step-title {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
}

.pcm-step-caption {
  margin: 0.15rem 0 1rem;
  font-size: 0.76rem;
  color: #6b7280;
}

.pcm-error-banner {
  margin: 0 0 0.85rem;
  padding: 0.5rem 0.7rem;
  border: 1px solid #fecaca;
  border-radius: 8px;
  background: #fef2f2;
  color: #b91c1c;
  font-size: 0.75rem;
  font-weight: 600;
}

.pcm-loading--failed {
  color: #b91c1c;
  font-weight: 600;
  text-align: center;
}

.pcm-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 180px;
  color: #6b7280;
  font-size: 0.8rem;
}

.pcm-spinner {
  width: 15px;
  height: 15px;
  border: 2px solid #d4ece1;
  border-top-color: #0e5b46;
  border-radius: 50%;
  animation: pcm-spin 0.7s linear infinite;
}

.pcm-spinner--sm {
  width: 12px;
  height: 12px;
  border-width: 2px;
  border-color: rgba(255, 255, 255, 0.4);
  border-top-color: #ffffff;
}

@keyframes pcm-spin {
  to { transform: rotate(360deg); }
}

/* ── Photo step ── */
.pcm-photo {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.7rem;
  padding: 1.1rem 1rem 1.25rem;
  border: 1px dashed #d8dde6;
  border-radius: 12px;
  background: #fbfcfe;
}

.pcm-photo__avatar {
  width: 116px;
  height: 116px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  color: #ffffff;
  font-size: 2.1rem;
  font-weight: 700;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.18);
}

.pcm-photo__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.pcm-photo__input {
  display: none;
}

.pcm-photo__stale {
  margin: 0;
  font-size: 0.72rem;
  font-weight: 600;
  color: #b45309;
  text-align: center;
}

.pcm-photo__hint {
  margin: 0;
  font-size: 0.72rem;
  color: #6b7280;
  text-align: center;
}

/* ── Fields ── */
.pcm-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem 0.85rem;
}

.pcm-field {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.pcm-field--wide {
  grid-column: 1 / -1;
}

.pcm-field__label {
  margin-bottom: 0.28rem;
  font-size: 0.72rem;
  font-weight: 600;
  color: #374151;
}

.pcm-field__req {
  color: #dc2626;
  font-weight: 700;
}

.pcm-input {
  width: 100%;
  height: 36px;
  padding: 0 0.65rem;
  border: 1px solid #d8dde6;
  border-radius: 8px;
  background: #ffffff;
  font-size: 0.8rem;
  color: #1f2a44;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.pcm-input--select {
  appearance: none;
  padding-right: 1.75rem;
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2.4' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.55rem center;
  background-size: 13px 13px;
  cursor: pointer;
}

.pcm-input--select:invalid {
  color: #9ca3af;
}

.pcm-input--textarea {
  height: auto;
  padding: 0.45rem 0.65rem;
  resize: vertical;
  line-height: 1.45;
}

.pcm-input:focus,
.pcm-input:focus-visible {
  outline: none;
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.16);
}

.pcm-input--error {
  border-color: #ef4444;
}

.pcm-input--error:focus {
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.14);
}

.pcm-field__hint,
.pcm-field__error {
  margin-top: 0.22rem;
  font-size: 0.68rem;
  line-height: 1.35;
}

.pcm-field__hint {
  color: #6b7280;
}

.pcm-field__error {
  color: #b91c1c;
  font-weight: 600;
}

.pcm-field__error--center {
  text-align: center;
}

/* ── Foot ── */
.pcm-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.8rem 1.35rem;
  border-top: 1px solid #eef1f7;
  background: #fbfcfe;
}

.pcm-foot__actions {
  display: flex;
  gap: 0.45rem;
}

.pcm-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  height: 34px;
  padding: 0 0.95rem;
  border-radius: 8px;
  border: 1px solid transparent;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.pcm-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.pcm-btn--primary {
  background: linear-gradient(135deg, #0a4a3a 0%, #0e5b46 55%, #0a4a3a 100%);
  border-color: #0a4a3a;
  color: #ffffff;
  box-shadow: 0 4px 12px rgba(10, 74, 58, 0.28);
}

.pcm-btn--primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #0d5b48 0%, #117057 55%, #0d5b48 100%);
}

.pcm-btn--ghost {
  background: #ffffff;
  border-color: #d4ece1;
  color: #0e5b46;
}

.pcm-btn--ghost:hover:not(:disabled) {
  background: #f5fbf8;
  border-color: #16a07a;
}

/* Design-system rule: never ship the UA's blue outline. Kill it on :focus,
   then put an on-brand ring back for keyboard users only. */
.pcm-btn:focus,
.pcm-link:focus,
.pcm-rail__item:focus {
  outline: none;
}

.pcm-btn::-moz-focus-inner,
.pcm-link::-moz-focus-inner,
.pcm-rail__item::-moz-focus-inner {
  border: 0;
}

.pcm-btn:focus-visible,
.pcm-link:focus-visible,
.pcm-rail__item:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.32);
}

.pcm-btn--primary:focus-visible {
  box-shadow: 0 4px 12px rgba(10, 74, 58, 0.28), 0 0 0 3px rgba(22, 160, 122, 0.32);
}

/* The overlay takes programmatic focus to seed the focus trap — it must
   never paint a focus ring of its own. */
.pcm-overlay:focus,
.pcm-overlay:focus-visible {
  outline: none;
}

.pcm-link {
  padding: 0;
  border: 0;
  background: none;
  color: #6b7280;
  font-size: 0.73rem;
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
  border-radius: 4px;
}

.pcm-link:hover:not(:disabled) {
  color: #b91c1c;
}

/* ── Transition ── */
.pcm-fade-enter-active,
.pcm-fade-leave-active {
  transition: opacity 0.22s ease;
}

.pcm-fade-enter,
.pcm-fade-leave-to {
  opacity: 0;
}

.pcm-fade-enter .pcm-panel,
.pcm-fade-leave-to .pcm-panel {
  transform: translateY(10px) scale(0.985);
}

.pcm-panel {
  transition: transform 0.24s cubic-bezier(0.22, 1, 0.36, 1);
}

@media (max-width: 640px) {
  .pcm-overlay {
    padding: 0;
  }

  .pcm-panel {
    max-width: none;
    max-height: 100vh;
    height: 100vh;
    border-radius: 0;
  }

  .pcm-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .pcm-foot {
    flex-direction: column-reverse;
    align-items: stretch;
  }

  .pcm-foot__actions {
    justify-content: stretch;
  }

  .pcm-foot__actions .pcm-btn {
    flex: 1 1 auto;
  }
}
</style>
